/**
 * The dashboard — the redesigned trading desk (promoted 9/25 from
 * /dashboard-next; the previous Dashboard remains at /dashboard-classic
 * for instant rollback).
 *
 * READ-ONLY BY DESIGN: every widget is a viewer; no write action exists
 * on this page (Stop buttons and order forms stay on their own pages —
 * the reused BotCards are rendered WITHOUT onStop). Its dark tokens
 * graduated to the global theme (src/theme.ts) on 10/25 — the whole app
 * now renders on them, so this page needs no local theme.
 *
 * Data: existing endpoints (realized-summary, bot list, history, marks,
 * account, WS) + /api/trade/benchmarks (PR #96) for the SPY/QQQ overlay.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip, ReferenceLine, Legend,
} from 'recharts';
import { useTradeStream, TradeEvent } from '../../hooks/useTradeStream';
import { useLiveAccount } from '../../hooks/useLiveAccount';
import {
  BotCard, Bot as RobotBot, TodaySignal, DayFees, LiveMark,
} from '../Robot/Robot';
import { MarkPoint } from '../Robot/BotPnlChart';
import { TrailEvent } from '../Robot/trail';
import { DayCondor } from '../Robot/UnderlyingQuote';
import { C, MONO } from '../../theme';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const LIVE_START = '2026-08-03';   // cloud live-trading cutover

const STRAT_SHORT: Record<string, string> = {
  IRON_CONDOR: 'IC', BREADTH_SPREAD: 'SPREAD', BREADTH_PUT: 'PUT',
  BREADTH_DIP: 'DIP', COVERED_CALL: 'CC', LONG_CALL_SLEEVE: 'SLEEVE', STRADDLE_BOOK: 'BOOK',
  TREND_OPTION: 'TREND',
};
const STRAT_NAME: Record<string, string> = {
  IRON_CONDOR: 'Iron Condor', BREADTH_SPREAD: 'Breadth Spread', BREADTH_PUT: 'Breadth Put',
  BREADTH_DIP: 'Breadth Dip', COVERED_CALL: 'Covered Call', LONG_CALL_SLEEVE: 'Call Sleeve',
  TREND_OPTION: 'Trend Option',
};

const fmt$ = (v: number | null | undefined, dec = 2) =>
  v == null ? '—' : `$${v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const fmtSigned = (v: number) =>
  `${v < 0 ? '−' : '+'}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}%`;

/* ---- shapes ---- */
interface TradeRow {
  id: string; botId?: string | null; symbol: string; secType: string; quantity: number;
  action: string; executedPrice?: number | null; executedQuantity?: number | null;
  status: string; createdAt: string;
}
interface Cycle { date: string; closedAt: string; symbol: string; strategy?: string; realizedPnL: number; }
interface FeedItem { at: string; hhmm: string; strategy: string; title: string; detail: string; pnl: number | null; pnlLabel?: string; }
interface CardData {
  bot: RobotBot & { holding?: boolean };
  marks: MarkPoint[];
  signal: TodaySignal | null;
  dayRealized: number | null;
  dayFees: DayFees | null;
  trailEvents: TrailEvent[];
  condor: DayCondor | null;
}
interface BenchPoint { date: string; port: number | null; spy: number | null; nasdaq: number | null; }

/* Friendly line for a bot event; null = not feed-worthy. */
function eventToFeed(type: string, p: Record<string, any>): Omit<FeedItem, 'at' | 'hhmm' | 'strategy'> | null {
  switch (type) {
    case 'SETUP':
      return { title: `Condor sold ${p.contracts}×`, detail: `${p.putSellStrike}P/${p.putBuyStrike} · ${p.callSellStrike}C/${p.callBuyStrike} · $${p.netCredit} cr`, pnl: null, pnlLabel: p.totalPremium ? `+$${p.totalPremium} cr` : undefined };
    case 'IC_CLOSED':
      return { title: 'Condor closed', detail: `filled $${p.fillPrice} db`, pnl: typeof p.realizedPnLTotal === 'number' ? p.realizedPnLTotal : null };
    case 'PROFIT_TARGET':
      return { title: 'Profit target 50%', detail: `closing ${p.contracts}× @ $${p.limitDebit} db`, pnl: null };
    case 'RATCHET_ARMED':
      return { title: 'Ratchet armed', detail: `at ${fmtSigned(p.armAtPnl ?? 0)}`, pnl: null };
    case 'TRAIL_ARMED':
      return { title: 'Trail armed', detail: `at ${fmtSigned(p.pnlAtArm ?? 0)} · target ${fmtSigned(p.targetPnl ?? 0)} · floor ${fmtSigned(p.floorPnl ?? 0)}`, pnl: null };
    case 'TRAIL_FLOOR':
      return { title: 'Trail floor exit', detail: p.limitDebit != null ? `closing ${p.contracts ?? ''}× @ $${p.limitDebit} db` : 'closing', pnl: null };
    case 'WING_SALVAGE_FILLED':
      return { title: 'Wing salvage', detail: `${p.occSymbol?.slice(-9) ?? ''} ×${p.qty} @ $${p.fillPrice}`, pnl: typeof p.realizedPnL === 'number' ? p.realizedPnL : null };
    case 'BREADTH_SPREAD_ENTRY':
      return { title: `Spread sold ${p.qty}×`, detail: `${p.shortStrike}C/${p.wingStrike} · $${p.credit} cr`, pnl: null, pnlLabel: p.creditUsd ? `+$${p.creditUsd} cr` : undefined };
    case 'BREADTH_PUT_ENTRY':
      return { title: `Put spread sold ${p.qty}×`, detail: `${p.shortStrike}P/${p.wingStrike} · $${p.credit} cr`, pnl: null, pnlLabel: p.creditUsd ? `+$${p.creditUsd} cr` : undefined };
    case 'BREADTH_SPREAD_EXIT':
    case 'BREADTH_PUT_EXIT':
      return { title: p.reason === 'ADVERSE_STOP' ? 'Adverse stop' : p.reason === 'FORCE_CLOSE' ? 'Force close 15:55' : p.reason === 'DIP_TARGET' ? 'Dip buyback' : 'Exit', detail: `closed $${p.closeCost}`, pnl: typeof p.realizedPnL === 'number' ? p.realizedPnL : null };
    case 'BREADTH_SPREAD_SIGNAL':
    case 'BREADTH_PUT_SIGNAL':
    case 'BREADTH_SIGNAL': {
      if (p.action === 'ENTERING') return null;
      const b = p.breadth ?? (p.reds != null ? 8 - p.reds : null);
      return { title: 'Stood down', detail: `${p.action?.toLowerCase().replace(/_/g, ' ')}${b != null ? ` · breadth ${p.breadth ?? b}/8` : ''}`, pnl: null, pnlLabel: 'no trade' };
    }
    case 'TREND_ENTRY':
      return { title: `${p.dir === 1 ? 'Call' : 'Put'} bought ${p.qty}×`, detail: `${p.strike}${p.dir === 1 ? 'C' : 'P'} @ $${p.fillPrice} · grid ${p.grid}`, pnl: null, pnlLabel: p.costUsd ? `−$${p.costUsd} db` : undefined };
    case 'TREND_EXIT':
      return { title: p.reason === 'EXIT_1530' ? 'Exit 15:30' : p.reason === 'BAND_STOP' ? 'Band stop' : p.reason === 'FORCE_CLOSE' ? 'Force close 15:50' : p.reason === 'OPERATOR_STOP' ? 'Operator stop' : 'Exit', detail: `sold @ $${p.exitPrice}${p.market ? ' (market)' : ''}`, pnl: typeof p.realizedPnL === 'number' ? p.realizedPnL : null };
    case 'TREND_SIGNAL': {
      if (!p.action || p.action === 'HOLD' || p.action === 'NONE') return null;
      if (p.action.startsWith('ENTER') || p.action.startsWith('EXIT')) return null; // the fill events carry these
      return { title: 'Grid check', detail: `${p.grid} · p ${p.p} · band ${p.lower}–${p.upper}`, pnl: null };
    }
    case 'TREND_SKIPPED':
      return { title: 'Session skipped', detail: `${String(p.reason ?? '').toLowerCase().replace(/_/g, ' ')}`, pnl: null, pnlLabel: 'no trade' };
    case 'BREADTH_SPREAD_EXPIRED':
    case 'BREADTH_PUT_EXPIRED':
      return { title: 'Expired worthless', detail: `${p.qty}× kept full credit`, pnl: typeof p.realizedPnL === 'number' ? p.realizedPnL : null };
    default:
      return null;
  }
}

const occPretty = (sym: string) => {
  const m = sym.match(/^([A-Z]+)(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/);
  if (!m) return sym;
  return `${m[1]} ${m[3]}/${m[4]} ${Number(m[6]) / 1000}${m[5]}`;
};

type RangeKey = '1D' | '5D' | '30D' | '1Y' | 'YTD' | 'LIVE';
const DOLLAR_RANGES: RangeKey[] = ['1D', '5D', '30D', '1Y'];

const DashboardNext: React.FC = () => {
  const { account } = useLiveAccount();
  const [bots, setBots] = useState<Array<RobotBot & { holding?: boolean; capitalReservation?: number | null; riskBudget?: number | null }>>([]);
  const [trades, setTrades] = useState<TradeRow[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [summary, setSummary] = useState<{ today: number; week: number; allTime: number; cycleCount: number; winCount: number; fees?: { today: number; week: number; allTime: number; basis: string }; capital?: { deposits: number; withdrawals: number; netContributed: number; firstTransfer: string | null } } | null>(null);
  const [fees, setFees] = useState<{ amount: number; basis: string } | null>(null);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [cards, setCards] = useState<CardData[]>([]);
  const [range, setRange] = useState<RangeKey>('30D');
  const [bench, setBench] = useState<Record<string, Record<string, Array<{ date: string; close: number }>>>>({});

  const load = useCallback(async () => {
    try {
      const [rs, bl, th] = await Promise.all([
        fetch(`${API}/api/trade/realized-summary`, { headers: auth() }).then(r => r.json()),
        fetch(`${API}/api/bot/list`, { headers: auth() }).then(r => r.json()),
        fetch(`${API}/api/trade/history`, { headers: auth() }).then(r => r.json()),
      ]);
      setCycles(rs.cycles || []); setSummary(rs.summary || null);
      const botRows = (bl.bots || []);
      setBots(botRows); setTrades((th.trades || []).slice(0, 8));

      const active = botRows.filter((b: any) => b.status === 'ACTIVE');
      const [markRes, detailRes] = await Promise.all([
        Promise.all(active.map((b: any) => fetch(`${API}/api/bot/${b.id}/marks`, { headers: auth() }).then(r => r.json()).catch(() => null))),
        Promise.all(active.map((b: any) => fetch(`${API}/api/bot/${b.id}`, { headers: auth() }).then(r => r.json()).catch(() => null))),
      ]);
      let feeSum = 0; let feeBasis: string | null = null; let any = false;
      const cardData: CardData[] = [];
      markRes.forEach((m: any, i: number) => {
        if (m?.dayFees?.amount) { feeSum += m.dayFees.amount; any = true;
          feeBasis = feeBasis && feeBasis !== m.dayFees.basis ? 'mixed' : m.dayFees.basis; }
        if (m && ((m.marks || []).length > 0 || m.dayRealized != null)) {
          cardData.push({
            bot: active[i], marks: m.marks || [], signal: m.todaySignal || null,
            dayRealized: m.dayRealized ?? null, dayFees: m.dayFees ?? null,
            trailEvents: m.dayTrail || [], condor: m.dayCondor ?? null,
          });
        }
      });
      setFees(any ? { amount: Math.round(feeSum * 100) / 100, basis: feeBasis || 'estimated' } : null);
      setCards(cardData);

      const today = new Date(); today.setHours(0, 0, 0, 0);
      const items: FeedItem[] = [];
      detailRes.forEach((d: any, i: number) => {
        (d?.events || []).forEach((ev: any) => {
          const t = new Date(ev.createdAt);
          if (t < today) return;
          const payload = typeof ev.payload === 'string' ? JSON.parse(ev.payload) : (ev.payload || {});
          const f = eventToFeed(ev.type, payload);
          if (f) items.push({ ...f, at: ev.createdAt, strategy: active[i].strategy,
            hhmm: t.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) });
        });
      });
      items.sort((a, b) => a.at.localeCompare(b.at));
      setFeed(items);
    } catch (e) { console.error('dashboard-next load error:', e); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Streamed marks arrive every 15-60s while anything holds — reload at
  // most every 15s so the page stays fresh without hammering the API.
  const lastLoadRef = useRef(0);
  const onEvent = useCallback((_ev: TradeEvent) => {
    const now = Date.now();
    if (now - lastLoadRef.current < 15_000) return;
    lastLoadRef.current = now;
    load();
  }, [load]);
  const { status: streamStatus } = useTradeStream(onEvent);

  /* ---- benchmark fetch (YTD / LIVE modes) ---- */
  const benchStart = range === 'YTD'
    ? `${new Date().getFullYear()}-01-01`
    : range === 'LIVE' ? LIVE_START : null;
  useEffect(() => {
    if (!benchStart || bench[benchStart]) return;
    fetch(`${API}/api/trade/benchmarks?start=${benchStart}`, { headers: auth() })
      .then(r => r.json())
      .then(j => setBench(prev => ({ ...prev, [benchStart]: j.series || {} })))
      .catch(() => setBench(prev => ({ ...prev, [benchStart]: {} })));
  }, [benchStart, bench]);

  /* ---- $-mode chart: session-axis cumulative realized ---- */
  const RANGE_DAYS: Record<'1D' | '5D' | '30D' | '1Y', number> = { '1D': 1, '5D': 5, '30D': 30, '1Y': 365 };
  const ranged = useMemo(() => {
    if (!DOLLAR_RANGES.includes(range)) return [];
    const r = range as '1D' | '5D' | '30D' | '1Y';
    let inWindow: Cycle[];
    if (r === '1D' || r === '5D') {
      const dates = Array.from(new Set(cycles.map(c => c.date)));
      const keep = new Set(dates.slice(-RANGE_DAYS[r]));
      inWindow = cycles.filter(c => keep.has(c.date));
    } else {
      const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
      const cutoff = midnight.getTime() - (RANGE_DAYS[r] - 1) * 86400000;
      inWindow = cycles.filter(c => new Date(c.closedAt).getTime() >= cutoff);
    }
    let run = 0;
    return inWindow.map(c => { run = Math.round((run + c.realizedPnL) * 100) / 100; return { ...c, cum: run }; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycles, range]);

  const axis = useMemo(() => {
    const sessions: string[] = []; const seen = new Set<string>();
    ranged.forEach(c => { if (!seen.has(c.date)) { seen.add(c.date); sessions.push(c.date); } });
    const idx = new Map(sessions.map((d, i) => [d, i]));
    const frac = (iso: string) => {
      const d = new Date(iso); const mins = d.getHours() * 60 + d.getMinutes();
      return Math.min(1, Math.max(0, (mins - 570) / 390));
    };
    const data = ranged.map(c => ({ ...c, x: (idx.get(c.date) ?? 0) + frac(c.closedAt) }));
    let ticks: number[] | undefined;
    if (!(range === '1D' || sessions.length <= 1)) {
      const step = Math.max(1, Math.ceil(sessions.length / 6));
      ticks = []; for (let i = 0; i < sessions.length; i += step) ticks.push(i);
    }
    const fmt = (v: number) => {
      if (range === '1D' || sessions.length <= 1) {
        const base = 570 + Math.round((v - Math.floor(v)) * 390);
        const h = Math.floor(base / 60), m = base % 60, h12 = ((h + 11) % 12) + 1;
        return `${h12}:${String(m).padStart(2, '0')}${h >= 12 ? 'p' : 'a'}`;
      }
      const d = sessions[Math.round(v)];
      return d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    };
    return { data, ticks, fmt, domain: [0, Math.max(sessions.length, 1)] as [number, number] };
  }, [ranged, range]);

  /* ---- %-mode chart: portfolio vs SPY vs Nasdaq-100 ---- */
  const benchData = useMemo((): BenchPoint[] => {
    if (!benchStart) return [];
    const series = bench[benchStart];
    if (!series) return [];
    const spy = series.SPY || [];
    const ndx = series.QQQ || [];
    const spy0 = spy[0]?.close, ndx0 = ndx[0]?.close;
    const byDate = new Map<string, BenchPoint>();
    spy.forEach(r => byDate.set(r.date, {
      date: r.date, spy: spy0 ? (r.close / spy0 - 1) * 100 : null, nasdaq: null, port: null }));
    ndx.forEach(r => {
      const row = byDate.get(r.date) || { date: r.date, spy: null, nasdaq: null, port: null };
      row.nasdaq = ndx0 ? (r.close / ndx0 - 1) * 100 : null;
      byDate.set(r.date, row);
    });
    const dates = Array.from(byDate.keys()).sort();
    // Portfolio: cumulative realized net within the window as % of the
    // window's starting equity (equity_now minus what the window earned
    // — a stated approximation; deposits/fees drift it slightly).
    const inWin = cycles.filter(c => c.date >= benchStart);
    const cumByDate = new Map<string, number>();
    let run = 0;
    inWin.forEach(c => { run = Math.round((run + c.realizedPnL) * 100) / 100; cumByDate.set(c.date, run); });
    const totalWin = run;
    const baseEquity = account?.equity != null ? account.equity - totalWin : null;
    let carry = 0;
    const out = dates.map(d => {
      if (cumByDate.has(d)) carry = cumByDate.get(d)!;
      const row = byDate.get(d)!;
      return { ...row, port: baseEquity ? (carry / baseEquity) * 100 : null };
    });
    // Today: benchmarks end yesterday; carry them flat and add today's
    // realized so the last point is current.
    const todayIso = new Date().toISOString().slice(0, 10);
    if (dates.length && dates[dates.length - 1] < todayIso && cumByDate.has(todayIso)) {
      const last = out[out.length - 1];
      out.push({ date: todayIso, spy: last.spy, nasdaq: last.nasdaq,
        port: baseEquity ? (cumByDate.get(todayIso)! / baseEquity) * 100 : null });
    }
    return out;
  }, [benchStart, bench, cycles, account]);

  /* ---- derived ---- */
  const dayDelta = account?.equity != null && account?.lastEquity != null
    ? account.equity - account.lastEquity : null;
  const activeBots = bots.filter(b => (b as any).status === 'ACTIVE');
  const claims = activeBots
    .map(b => ({ b, claim: (b as any).capitalReservation ?? (b as any).riskBudget ?? null }))
    .filter((x): x is { b: typeof activeBots[number]; claim: number } => x.claim != null && x.claim > 0);
  const claimed = claims.reduce((s, x) => s + x.claim, 0);
  const equity = account?.equity ?? null;
  const free = equity != null ? Math.max(0, equity - claimed) : null;
  const allocTotal = equity != null && equity > 0 ? equity : claimed || 1;
  const stratOf = (t: TradeRow) => (bots.find(b => b.id === t.botId) as any)?.strategy;
  const winRate = summary && summary.cycleCount > 0 ? Math.round(100 * summary.winCount / summary.cycleCount) : null;
  const todayIso = new Date().toISOString().slice(0, 10);

  const chip = (strategy?: string) => strategy ? (
    <Box component="span" sx={{
      fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', px: '7px', py: '1px',
      borderRadius: '5px', color: C.s[strategy] || C.ink2,
      bgcolor: `${(C.s[strategy] || '#666')}24`,
    }}>{STRAT_SHORT[strategy] || strategy}</Box>
  ) : null;
  const K = ({ children }: { children: React.ReactNode }) => (
    <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: C.ink3 }}>{children}</Typography>
  );
  /* Total gain = what the account is worth minus what was put in (the
     broker ledger's cash transfers); realized P&L is a different ledger. */
  const capital = summary?.capital && summary.capital.netContributed > 0 ? summary.capital : null;
  const totalGain = capital && equity != null ? equity - capital.netContributed : null;

  const panelSx = { bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: '14px', p: '18px 20px 16px', minWidth: 0 };

  /* Live mark for a reused card: last streamed row, only while fresh. */
  const liveMarkOf = (cd: CardData): LiveMark | undefined => {
    const last: any = cd.marks[cd.marks.length - 1];
    if (!last || last.unrealizedPnl == null || last.targetPnl == null) return undefined;
    const ageMs = Date.now() - new Date(last.at).getTime();
    if (ageMs > 3 * 60_000) return undefined;
    return { ...last, receivedAt: Date.now() } as LiveMark;
  };

  return (
    <Box sx={{ color: C.ink, minWidth: 0 }}>

      {/* header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Trading Desk</Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: streamStatus === 'open' ? C.pos : C.warn, boxShadow: streamStatus === 'open' ? `0 0 8px ${C.pos}` : 'none' }} />
          <Typography sx={{ fontSize: 12.5, color: C.ink2 }}>{streamStatus === 'open' ? 'streaming' : streamStatus}</Typography>
        </Box>
      </Box>

      {/* hero */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '40px', flexWrap: 'wrap', py: '18px' }}>
        <Box>
          <K>Account equity</K>
          <Typography sx={{ fontFamily: MONO, fontWeight: 600, fontSize: { xs: 32, md: 40 }, lineHeight: 1.1, mt: '4px', fontVariantNumeric: 'tabular-nums' }}>
            {fmt$(equity)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '7px' }}>
            {dayDelta != null && (
              <Typography sx={{ fontFamily: MONO, fontSize: 14, fontWeight: 500, color: dayDelta >= 0 ? C.pos : C.neg }}>
                {fmtSigned(dayDelta)}{account?.lastEquity ? ` (${(100 * dayDelta / account.lastEquity!).toFixed(2)}%)` : ''} today
              </Typography>
            )}
          </Box>
          {capital && equity != null && (
            <Typography sx={{ fontSize: 13, mt: '8px', color: C.ink2 }}>
              <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: totalGain! >= 0 ? C.pos : C.neg }}>
                {fmtSigned(totalGain!)} ({fmtPct(100 * totalGain! / capital.netContributed)})
              </Box>
              {' '}total gain on <Box component="span" sx={{ fontFamily: MONO }}>{fmt$(capital.netContributed, 0)}</Box> contributed
              {capital.firstTransfer && <Box component="span" sx={{ color: C.ink3 }}> · since {new Date(capital.firstTransfer + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Box>}
            </Typography>
          )}
          <Typography sx={{ color: C.ink3, fontSize: 12.5, mt: '6px' }}>
            all-time realized <Box component="span" sx={{ fontFamily: MONO }}>{summary ? fmtSigned(summary.allTime) : '—'}</Box>
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1px', bgcolor: C.line, border: `1px solid ${C.line}`, borderRadius: '12px', overflow: 'hidden', flex: '1 1 520px', minWidth: 0 }}>
          {[
            { k: 'Deposited', v: capital ? fmt$(capital.netContributed, 0) : '—', sub: capital && capital.withdrawals > 0 ? 'net' : undefined },
            { k: 'Total gain', v: totalGain != null ? fmtSigned(totalGain) : '—', c: totalGain != null ? (totalGain >= 0 ? C.pos : C.neg) : undefined,
              sub: totalGain != null && capital ? fmtPct(100 * totalGain / capital.netContributed) : undefined },
            { k: 'Cash', v: fmt$(account?.cash ?? null, 0) },
            { k: 'Options BP', v: fmt$(account?.optionsBuyingPower ?? null, 0) },
            { k: 'Realized today', v: summary ? fmtSigned(summary.today) : '—', c: summary ? (summary.today >= 0 ? C.pos : C.neg) : undefined },
            { k: 'Fees today', v: fees ? `$${fees.amount.toFixed(2)}` : '—', sub: fees?.basis === 'estimated' ? 'est.' : fees?.basis === 'apportioned' ? 'apport.' : undefined },
            // Net of posted fees (summary.fees is actual-basis, posts T+1) —
            // the same net-first rule as every other P&L figure on the page.
            { k: 'Realized · week', v: summary ? fmtSigned(summary.week - (summary.fees?.week ?? 0)) : '—', sub: summary?.fees ? 'net' : undefined, c: summary ? ((summary.week - (summary.fees?.week ?? 0)) >= 0 ? C.pos : C.neg) : undefined },
            { k: 'Win rate', v: winRate != null ? `${winRate}%` : '—', sub: summary ? `${summary.cycleCount} cycles` : undefined },
          ].map(cell => (
            // minWidth 0 lets a grid item shrink below its content so a long
            // value wraps instead of overflowing into the strip's overflow:hidden
            // (the +$x,xxx +y.y% total-gain crop); the secondary figure gets its
            // own line for the same reason.
            <Box key={cell.k} sx={{ bgcolor: C.panel, p: '13px 16px 12px', minWidth: 0 }}>
              <K>{cell.k}</K>
              <Typography sx={{ fontFamily: MONO, fontSize: 17, fontWeight: 500, mt: '3px', fontVariantNumeric: 'tabular-nums', color: cell.c || C.ink, overflowWrap: 'anywhere', lineHeight: 1.25 }}>
                {cell.v}
              </Typography>
              {cell.sub && (
                <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: C.ink3, mt: '2px', overflowWrap: 'anywhere' }}>{cell.sub}</Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* main grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) 360px' }, gap: '18px', alignItems: 'start' }}>

        {/* left column: chart + traded-today cards */}
        <Box sx={{ display: 'grid', gap: '18px', minWidth: 0 }}>
          <Box sx={panelSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '6px' }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                {benchStart
                  ? <>Portfolio vs benchmarks <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: C.ink3 }}>% return · realized-only vs index price</Box></>
                  : <>Cumulative realized P&L <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: C.ink3 }}>closed cycles</Box></>}
              </Typography>
              <Box sx={{ display: 'flex', gap: '2px', bgcolor: C.panel2, border: `1px solid ${C.line}`, borderRadius: '8px', p: '2px' }}>
                {(['1D', '5D', '30D', '1Y', 'YTD', 'LIVE'] as RangeKey[]).map(r => (
                  <Box key={r} component="button" onClick={() => setRange(r)} sx={{
                    all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 600, px: '10px', py: '4px', borderRadius: '6px',
                    color: range === r ? C.ink : C.ink3, bgcolor: range === r ? 'rgba(57,135,229,.18)' : 'transparent',
                    '&:hover': { color: range === r ? C.ink : C.ink2 },
                  }}>{r}</Box>
                ))}
              </Box>
            </Box>

            {benchStart ? (
              benchData.length === 0 ? (
                <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: C.ink3, fontSize: 13 }}>Loading benchmark data…</Typography>
                </Box>
              ) : (
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={benchData} margin={{ top: 8, right: 14, bottom: 4, left: 4 }}>
                      <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: C.ink3, fontFamily: MONO }}
                        tickLine={false} axisLine={false} minTickGap={48}
                        tickFormatter={(d: string) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                      <YAxis tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                        tick={{ fontSize: 11, fill: C.ink3, fontFamily: MONO }} tickLine={false} axisLine={false} width={52} />
                      <ReferenceLine y={0} stroke="rgba(255,255,255,.18)" strokeDasharray="1 3" />
                      <RTooltip
                        contentStyle={{ background: '#1A2130', border: `1px solid ${C.line2}`, borderRadius: 8, fontFamily: MONO, fontSize: 12 }}
                        labelStyle={{ color: C.ink3 }}
                        formatter={(value: any, name: any) => [fmtPct(Number(value)), name]}
                        labelFormatter={(d: any) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
                      <Legend wrapperStyle={{ fontSize: 12, fontFamily: MONO }} iconType="plainline" />
                      <Line type="monotone" dataKey="port" name="Portfolio" stroke={C.accent} strokeWidth={2} dot={false} connectNulls />
                      <Line type="monotone" dataKey="spy" name="S&P 500 (SPY)" stroke={C.spy} strokeWidth={1.5} dot={false} connectNulls />
                      <Line type="monotone" dataKey="nasdaq" name="Nasdaq-100 (QQQ)" stroke={C.nasdaq} strokeWidth={1.5} dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )
            ) : axis.data.length === 0 ? (
              <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: C.ink3, fontSize: 13 }}>No closed cycles in this range</Typography>
              </Box>
            ) : (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={axis.data} margin={{ top: 8, right: 14, bottom: 4, left: 4 }}>
                    <defs>
                      <linearGradient id="dn-cum" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.accent} stopOpacity={0.22} />
                        <stop offset="100%" stopColor={C.accent} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
                    <XAxis dataKey="x" type="number" domain={axis.domain} ticks={axis.ticks} tickFormatter={axis.fmt}
                      tick={{ fontSize: 11, fill: C.ink3, fontFamily: MONO }} tickLine={false} axisLine={false} minTickGap={40} />
                    <YAxis tickFormatter={(v: number) => `$${Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
                      tick={{ fontSize: 11, fill: C.ink3, fontFamily: MONO }} tickLine={false} axisLine={false} width={52} />
                    <ReferenceLine y={0} stroke="rgba(255,255,255,.18)" strokeDasharray="1 3" />
                    <RTooltip
                      contentStyle={{ background: '#1A2130', border: `1px solid ${C.line2}`, borderRadius: 8, fontFamily: MONO, fontSize: 12 }}
                      labelStyle={{ color: C.ink3 }} itemStyle={{ color: C.ink }}
                      formatter={(value: any) => [fmtSigned(Number(value)), 'cumulative']}
                      labelFormatter={(_v: any, payload: any) => {
                        const p = payload?.[0]?.payload; if (!p) return '';
                        const when = new Date(p.closedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                        return `${when} · ${STRAT_SHORT[p.strategy] || ''} cycle ${fmtSigned(p.realizedPnL)}`;
                      }} />
                    <Area type="monotone" dataKey="cum" stroke={C.accent} strokeWidth={2} fill="url(#dn-cum)" dot={false}
                      activeDot={{ r: 4, fill: C.accent, stroke: C.ink }} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          {/* traded-today cards — the REAL Robot cards, read-only (no Stop) */}
          {cards.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: '10px' }}>
                Traded today <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: C.ink3 }}>
                  bots with activity · read-only</Box>
              </Typography>
                <Box sx={{ display: 'grid', gap: '12px' }}>
                  {cards.map(cd => (
                    <BotCard
                      key={cd.bot.id}
                      bot={cd.bot as RobotBot}
                      mark={liveMarkOf(cd)}
                      seriesPoints={cd.marks}
                      dayKey={todayIso}
                      signal={cd.signal}
                      dayRealized={cd.dayRealized}
                      dayFees={cd.dayFees}
                      trailEvents={cd.trailEvents}
                      condor={cd.condor}
                      live={true}
                      selected={false}
                      stopping={false}
                      onClick={() => {}}
                    />
                  ))}
                </Box>
            </Box>
          )}
        </Box>

        {/* right column */}
        <Box sx={{ display: 'grid', gap: '18px', minWidth: 0 }}>

          <Box sx={panelSx}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
              Capital allocation <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: C.ink3 }}>claims vs. free cash</Box>
            </Typography>
            <Box sx={{ display: 'flex', height: 14, borderRadius: '7px', overflow: 'hidden', gap: '2px', bgcolor: C.bg, my: '14px' }}>
              {claims.map(({ b, claim }) => (
                <Box key={b.id} sx={{ width: `${(100 * claim / allocTotal).toFixed(1)}%`, bgcolor: C.s[(b as any).strategy] || C.ink3 }} />
              ))}
              <Box sx={{ flex: 1, bgcolor: C.neutral }} />
            </Box>
            <Box>
              {claims.map(({ b, claim }) => (
                <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '7px', borderBottom: `1px solid ${C.line}` }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: C.s[(b as any).strategy] || C.ink3, flex: '0 0 auto' }} />
                  <Typography sx={{ fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>{STRAT_NAME[(b as any).strategy] || (b as any).strategy}</Typography>
                  {(b as any).holding && <Box sx={{ fontSize: 10.5, fontWeight: 600, color: C.pos, border: `1px solid ${C.pos}44`, px: '6px', borderRadius: '5px' }}>HOLDING</Box>}
                  <Typography sx={{ ml: 'auto', fontFamily: MONO, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{fmt$(claim, 0)}</Typography>
                  <Typography sx={{ width: 40, textAlign: 'right', fontFamily: MONO, fontSize: 12, color: C.ink3 }}>{Math.round(100 * claim / allocTotal)}%</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '7px' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: C.neutral, flex: '0 0 auto' }} />
                <Typography sx={{ fontWeight: 500, fontSize: 13, color: C.ink2 }}>Unallocated</Typography>
                <Typography sx={{ ml: 'auto', fontFamily: MONO, fontSize: 13, color: C.ink2 }}>{fmt$(free, 0)}</Typography>
                <Typography sx={{ width: 40, textAlign: 'right', fontFamily: MONO, fontSize: 12, color: C.ink3 }}>
                  {free != null ? `${Math.round(100 * free / allocTotal)}%` : '—'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={panelSx}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
              Today's session <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: C.ink3 }}>
                {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</Box>
            </Typography>
            <Box sx={{ mt: '4px' }}>
              {feed.length === 0 && <Typography sx={{ color: C.ink3, fontSize: 13, py: 1 }}>No events yet today.</Typography>}
              {feed.map((f, i) => (
                <Box key={i} sx={{ display: 'flex', gap: '12px', py: '9px', borderBottom: i < feed.length - 1 ? `1px solid ${C.line}` : 0, alignItems: 'baseline' }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: 12, color: C.ink3, width: 44, flex: '0 0 auto' }}>{f.hhmm}</Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{f.title} {chip(f.strategy)}</Typography>
                    <Typography sx={{ fontSize: 12, color: C.ink3, fontFamily: MONO }}>{f.detail}</Typography>
                  </Box>
                  <Typography sx={{ ml: 'auto', fontFamily: MONO, fontSize: 13, fontWeight: 600, flex: '0 0 auto',
                    color: f.pnl != null ? (f.pnl >= 0 ? C.pos : C.neg) : C.ink3 }}>
                    {f.pnl != null ? fmtSigned(f.pnl) : (f.pnlLabel || '')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* fills */}
      <Box sx={{ ...panelSx, mt: '18px' }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: '6px' }}>
          Recent fills <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: C.ink3 }}>last 8</Box>
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 640,
            '& th': { textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: C.ink3, p: '8px 12px', borderBottom: `1px solid ${C.line2}`, whiteSpace: 'nowrap' },
            '& td': { p: '9px 12px', borderBottom: `1px solid ${C.line}`, fontSize: 13, whiteSpace: 'nowrap', color: C.ink },
            '& tbody tr:last-child td': { borderBottom: 0 },
            '& tbody tr:hover td': { bgcolor: 'rgba(255,255,255,.025)' },
          }}>
            <thead><tr>
              <th>Time</th><th>Strategy</th><th>Instrument</th><th>Side</th>
              <th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Fill</th><th>Status</th>
            </tr></thead>
            <tbody>
              {trades.map(t => {
                const sell = t.action.startsWith('SELL');
                return (
                  <tr key={t.id}>
                    <td style={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
                      {new Date(t.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                    </td>
                    <td>{chip(stratOf(t)) || <span style={{ color: C.ink3 }}>manual</span>}</td>
                    <td style={{ fontFamily: MONO, fontSize: 12.5, color: C.ink2 }}>{t.secType === 'OPT' ? occPretty(t.symbol) : t.symbol}</td>
                    <td><Box component="span" sx={{ fontSize: 11, fontWeight: 600, px: '7px', py: '2px', borderRadius: '5px',
                      color: sell ? C.neg : C.pos, bgcolor: sell ? 'rgba(230,103,103,.10)' : 'rgba(53,196,141,.10)' }}>
                      {sell ? 'SELL' : 'BUY'}</Box></td>
                    <td style={{ fontFamily: MONO, textAlign: 'right' }}>{t.executedQuantity ?? t.quantity}</td>
                    <td style={{ fontFamily: MONO, textAlign: 'right' }}>{t.executedPrice != null ? Number(t.executedPrice).toFixed(2) : '—'}</td>
                    <td><Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 12, color: C.ink2 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%',
                        bgcolor: t.status === 'EXECUTED' ? C.pos : t.status === 'PENDING' ? C.warn : C.neg }} />
                      {t.status === 'EXECUTED' ? 'Filled' : t.status.toLowerCase()}</Box></td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </Box>
      </Box>

      <Typography sx={{ mt: '18px', fontSize: 12, color: C.ink3 }}>
        Read-only overview · trade actions live on the Bots and Trade pages
      </Typography>
    </Box>
  );
};

export default DashboardNext;
